import { FloatInput } from "@jewlr/storybook/form"
import PropTypes from "prop-types"
import { memo, useEffect, useRef, useState } from "react"
import { connect } from "react-redux"

import {
  clearDefault,
  clearEngravingPos,
  forceHideMiniview,
  showMiniview,
  trackSlide,
  updateChoice,
} from "areas/product/store/actions"
import { to_currency } from "helpers/number"
import limitChars from "helpers/product/limit-chars"
import { cn } from "styles/utils"

import Keyboard from "./keyboard"

const InnerEntry = ({
  data,
  dispatchEngravingValue,
  doOnBlur,
  doOnFocus,
  engraving,
  engravingUpdateCount,
  engravingValue,
  enterInput,
  // enterKeyUp,
  enterPaste,
  isHebrewFont,
  maxChars,
  modal,
}) => {
  const [cursorPos, setCursorPos] = useState(null)
  const [fastEngravingValue, setFastEngravingValue] = useState(engravingValue)
  const loaded = useRef(false)
  const handler = useRef()

  useEffect(() => {
    loaded.current = true
  }, [])

  // Update fastEngravingValue when the engraving updateCount in state.choices is changed from font select
  useEffect(() => {
    setFastEngravingValue(engravingValue)
  }, [engravingUpdateCount])

  useEffect(() => {
    if (engravingValue !== fastEngravingValue) {
      if (cursorPos || fastEngravingValue != "") {
        handler.current = setTimeout(() => {
          dispatchEngravingValue(fastEngravingValue, engraving, loaded.current)
        }, 300)
      }

      return () => {
        if (handler.current) {
          clearTimeout(handler.current)
        }
      }
    }
  }, [engravingValue, fastEngravingValue])

  const lengthRemaining = Math.max(maxChars - fastEngravingValue.length, 0)

  return (
    <>
      <div
        className={cn(
          "mx-auto mb-16 flex w-full items-center",
          modal && "tablet:w-(--tablet-width)"
        )}
        style={{ "--tablet-width": "370px" }}
      >
        <div className={cn("flex-1", modal ? "mr-20" : "mr-16")}>
          <FloatInput
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            data-cy={`product-wizard-engraving-input-${engraving.pref}`}
            id={`product-wizard-engraving-input-${engraving.pref}`}
            label={`${
              engraving.title ||
              (engraving.placeholder == data.heading.title
                ? ""
                : engraving.placeholder || "")
            }${
              engraving.price > 0 ? ` (+${to_currency(engraving.price)})` : ""
            }`}
            onBlur={() => doOnBlur()}
            onFocus={() => doOnFocus()}
            onInput={(e) => {
              enterInput(engraving, e, setFastEngravingValue)
              setCursorPos(rgetCursorPos(e))
            }}
            // onKeyUp={(e) => enterKeyUp(engraving, e)}
            onPaste={(e) => {
              enterPaste(engraving, e, setFastEngravingValue)
              setCursorPos(rgetCursorPos(e))
            }}
            placeholder={
              engraving.placeholder == data.heading.title
                ? engraving.placeholder || ""
                : ""
            }
            ref={(input) => {
              // engravingRefs[engraving.pref] = input
              if (input) {
                rrestoreCursorPos(input.inputRef, cursorPos)
              }
            }}
            showClear
            spellCheck="false"
            style={
              ["Arabic", "Hebrew"].includes(engraving.caps)
                ? { direction: "rtl" }
                : {}
            }
            type="text"
            value={fastEngravingValue}
            variant={modal ? "checkout" : "default"}
            {...(isHebrewFont ? { dir: "auto" } : {})}
          />
        </div>
        <span className="text-right" style={{ width: "40px" }}>
          {lengthRemaining.toString()} left
        </span>
      </div>

      {data.keyboard && (
        <Keyboard
          enterInput={enterInput}
          setFastEngravingValue={setFastEngravingValue}
          type={data.keyboard}
        />
      )}
    </>
  )
}

const MemoEntry = memo(InnerEntry, (prevProps, nextProps) => {
  /*
  when true, the props are considered "equal" and will not trigger a re-render of this component
  */
  return (
    prevProps.engravingUpdateCount == nextProps.engravingUpdateCount &&
    prevProps.styleCode == nextProps.styleCode &&
    prevProps.prefStore == nextProps.prefStore &&
    prevProps.engravingValue == nextProps.engravingValue
  )
})

const rgetCursorPos = (e) => {
  let el = e.target
  var start = 0,
    end = 0,
    range,
    textInputRange,
    len,
    endRange

  if (
    typeof el.selectionStart == "number" &&
    typeof el.selectionEnd == "number"
  ) {
    start = el.selectionStart
    end = el.selectionEnd
  } else {
    range = document.selection.createRange()

    if (range && range.parentElement() == el) {
      len = el.value.length

      // Create a working TextRange that lives only in the input
      textInputRange = el.createTextRange()
      textInputRange.moveToBookmark(range.getBookmark())

      // Check if the start and end of the selection are at the very end
      // of the input, since moveStart/moveEnd doesn't return what we want
      // in those cases
      endRange = el.createTextRange()
      endRange.collapse(false)

      if (textInputRange.compareEndPoints("StartToEnd", endRange) > -1) {
        start = end = len
      } else {
        start = -textInputRange.moveStart("character", -len)

        if (textInputRange.compareEndPoints("EndToEnd", endRange) > -1) {
          end = len
        } else {
          end = -textInputRange.moveEnd("character", -len)
        }
      }
    }
  }

  return {
    end: end,
    start: start,
  }
}

const rrestoreCursorPos = (el, pos) => {
  if (typeof pos === "undefined" || !pos) {
    return
  }
  if (!el) {
    return
  }
  if (el != document.activeElement) {
    return
  }
  if (
    typeof el.selectionStart == "number" &&
    typeof el.selectionEnd == "number"
  ) {
    el.selectionStart = pos.start
    el.selectionEnd = pos.end
  } else {
    var range = el.createTextRange()
    range.collapse(true)
    if (pos.start == pos.end) {
      range.move("character", pos.start)
    } else {
      range.moveEnd("character", pos.end)
      range.moveStart("character", pos.start)
    }
    range.select()
  }
}

const getEngravingValue = (state, ownProps) => {
  let value = ""
  const engraving = ownProps.engraving
  if (
    state.choices[engraving.pref] &&
    !state.choices[engraving.pref].is_default
  ) {
    value = state.choices[engraving.pref].value || ""
  }
  return value
}

const mapStateToProps = (state, ownProps) => {
  let maxChars = ownProps.adminMode
    ? ownProps.engraving.admin_max_chars
    : ownProps.engraving.max_chars
  if (ownProps.engraving.caps == "Arabic") {
    maxChars = 1
  }

  const isHebrewFont =
    state.choices.ef?.value &&
    ownProps.engraving.font_specific_overrides[state.choices.ef.value]
      ?.is_hebrew_font

  return {
    engravingUpdateCount: state.choices[ownProps.engraving.pref]
      ? state.choices[ownProps.engraving.pref].updateCount || 0
      : 0,
    engravingValue: getEngravingValue(state, ownProps),
    isDefault: state.choices[ownProps.engraving.pref]
      ? state.choices[ownProps.engraving.pref].is_default
      : false,
    isHebrewFont,
    maxChars: maxChars,
    prefStore: state.wizardUI.prefStore,
    productType: state.wizard.description.product_type,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch,
    dispatchEngravingValue: (engravingValue, engraving, fromUser) => {
      dispatch(clearEngravingPos(engraving.pref))
      if (engravingValue.length == 0) {
        dispatch(
          updateChoice(
            engraving.pref,
            {
              is_default: false,
              pref: engraving.pref,
              price: 0,
              value: engravingValue,
            },
            fromUser
          )
        )
      } else {
        dispatch(
          updateChoice(
            engraving.pref,
            Object.assign({}, engraving, {
              // cursor_position: pos,
              is_default: false,
              value: engravingValue,
            }),
            fromUser
          )
        )
      }
    },
    doOnBlur: () => {
      dispatch(showMiniview(true))
      dispatch(forceHideMiniview("scroll", false))
    },
    doOnFocus: () => {
      dispatch(clearDefault())
      dispatch(showMiniview(false))
      dispatch(forceHideMiniview("scroll", true))
    },
    // enterKeyUp: (engraving, e, setFastEngravingValue) => {
    //   let charCode = e.keyCode || e.which
    //   if (
    //     charCode != 16 &&
    //     charCode != 17 &&
    //     charCode != 18 &&
    //     charCode != 37 &&
    //     charCode != 38 &&
    //     charCode != 39 &&
    //     charCode != 40 &&
    //     charCode != 91 &&
    //     charCode != 93
    //   ) {
    //     if (charCode == 13) {
    //       e.currentTarget.blur()
    //     }
    //     engraving.is_default = false
    //     limitChars(engraving, e, dispatch, setFastEngravingValue)
    //   } else {
    //     dispatch(clearEngravingPos(""))
    //   }
    // },
  }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { productType } = stateProps
  const { dispatch } = dispatchProps
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    enterInput: (engraving, e, setFastEngravingValue) => {
      limitChars(engraving, e, dispatch, setFastEngravingValue)
      if (engraving.track_slide) {
        dispatch(trackSlide(engraving.track_slide))
      } else if (productType === "WATCH") {
        dispatch(trackSlide("b"))
      }
    },
    enterPaste: (engraving, e, setFastEngravingValue) => {
      limitChars(engraving, e, dispatch, setFastEngravingValue)
      if (engraving.track_slide) {
        dispatch(trackSlide(engraving.track_slide))
      } else if (productType === "WATCH") {
        dispatch(trackSlide("b"))
      }
    },
  }
}

const Entry = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(MemoEntry)

InnerEntry.propTypes = {
  data: PropTypes.object,
  dispatchEngravingValue: PropTypes.func,
  doOnBlur: PropTypes.func,
  doOnFocus: PropTypes.func,
  engraving: PropTypes.object,
  engravingUpdateCount: PropTypes.number,
  engravingValue: PropTypes.string,
  enterInput: PropTypes.func,
  // enterKeyUp: PropTypes.func,
  enterPaste: PropTypes.func,
  isDefault: PropTypes.bool,
  isHebrewFont: PropTypes.bool,
  maxChars: PropTypes.number,
  modal: PropTypes.bool,
}
export default Entry
